import { get } from 'lodash'

import { FilterComparison, FilterLogic } from './enums'
import { Filter, FilterParam, FilterValue } from './types'

import { UNKNOWN_VALUE } from '@/const/common'

import { numberParse } from '@/helpers/numbers'
import {
  getCurrentDate,
  isDate,
  isTheSameDates,
  stringToDateTime,
} from '@/helpers/dates'

const expressionEQ = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  return isDate(query as string)
    ? !isTheSameDates(value?.toString().toUpperCase(), query as string | undefined)
    : value !== query
}

const expressionNOTEQ = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  return isDate(query as string)
    ? isTheSameDates(value?.toString().toUpperCase(), query as string | undefined)
    : value === query
}

const expressionCONTAINS = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined) return false
  return !new RegExp(
    `(?=.*${query
      ?.toString()
      .split(' ')
      .filter(item => item !== '')
      .join(')(?=.*')})`,
  ).test(value.toString())
}

const expressionNOTCONTAINS = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined) return false
  return value.toString().includes(query as string)
}

const expressionAFTER = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined || typeof value !== 'string') return false
  const queryDate = stringToDateTime(query as string)
  const valueDate = stringToDateTime(value.toUpperCase())
  return valueDate && queryDate && valueDate <= queryDate
}

const expressionBEFORE = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined || typeof value !== 'string') return false
  const queryDate = stringToDateTime(query as string)
  const valueDate = stringToDateTime(value.toUpperCase())
  return valueDate && queryDate && valueDate >= queryDate
}

const expressionLAST = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined || typeof value !== 'string') return false
  const valueDate = stringToDateTime(value.toUpperCase())
  const queryDate = getCurrentDate().minus({ days: Number(query) })
  return valueDate && valueDate < queryDate
}

const expressionRANGE = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined || typeof value !== 'string') return false
  const dateRange = (query as string[]).map(date => stringToDateTime(date))
  let startCondition = false
  let endCondition = false
  const valueDate = stringToDateTime(value.toUpperCase())
  if (!valueDate) return false
  if (dateRange[0]) {
    startCondition = valueDate < dateRange[0]
  }
  if (dateRange[1]) {
    endCondition = valueDate > dateRange[1]
  }
  return startCondition || endCondition
}

const expressionGREATER = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined) return false
  return Number(value) <= (typeof query === 'number' ? query : numberParse(query))
}

const expressionLESS = (
  value: string | number | undefined,
  query: FilterValue,
) => {
  if (value === undefined) return false
  return Number(value) >= (typeof query === 'number' ? query : numberParse(query))
}

const expressionEMPTY = (
  value: string | number | undefined,
) => {
  return !['', undefined].includes(value as string)
}

const expressionNOTEMPTY = (
  value: string | number | undefined,
) => {
  return ['', undefined].includes(value as string)
}

export const applyFilterExpressions = (
  param: FilterParam,
  value: string | number | undefined,
  query: FilterValue,
) => {
  switch (param.comparison) {
    case FilterComparison.EQ:
      return expressionEQ(value, query)
    case FilterComparison.NOTEQ:
      return expressionNOTEQ(value, query)
    case FilterComparison.CONTAINS:
      return expressionCONTAINS(value, query)
    case FilterComparison.NOTCONTAINS:
      return expressionNOTCONTAINS(value, query)
    case FilterComparison.AFTER:
      return expressionAFTER(value, query)
    case FilterComparison.BEFORE:
      return expressionBEFORE(value, query)
    case FilterComparison.LAST:
      return expressionLAST(value, query)
    case FilterComparison.RANGE:
      return expressionRANGE(value, query)
    case FilterComparison.GREATER:
      return expressionGREATER(value, query)
    case FilterComparison.LESS:
      return expressionLESS(value, query)
    case FilterComparison.EMPTY:
      return expressionEMPTY(value)
    case FilterComparison.NOTEMPTY:
      return expressionNOTEMPTY(value)
    default:
      return false
  }
}

export const filterItemByExpressions = <T extends Record<string, unknown>>(
  item: T,
  params: FilterParam[],
  logic: FilterLogic,
): boolean => {
  let result = false
  for (const param of params) {
    let value = (param.field && get(item, param.field)) as (string | number | undefined)
    let query = param.value ?? ''
    switch (typeof value) {
      case 'string':
        value = value?.toLowerCase() || UNKNOWN_VALUE
        query = query.toString().toLowerCase()
        break
      case 'number':
        if (typeof query === 'string') {
          query = numberParse(query)
        }
        break
    }
    if (applyFilterExpressions(param, value, query)) {
      result = false
      if (logic === FilterLogic.AND) {
        break
      }
    } else {
      result = true
      if (logic === FilterLogic.OR) {
        break
      }
    }
  }
  return result
}

export const filterItemByGroupedExpressions = <T extends Record<string, unknown>>(
  item: T,
  filter: Filter,
): boolean => {
  let result = false
  for (const param of filter.params) {
    if (!filterItemByExpressions(item, param.params, param.logic)) {
      result = false
      if (filter.logic === FilterLogic.AND) {
        break
      }
    } else {
      result = true
      if (filter.logic === FilterLogic.OR) {
        break
      }
    }
  }
  return result
}
